import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Modal from '../components/UI/Modal';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', parent_id: '', gst_rate_id: '', hsn_code: '', status: 'active' });
  const [gstRates, setGstRates] = useState([]);

  const fetch = async () => {
    try {
      setLoading(true);
      const res = await api.get('/categories/list.php');
      if (res.data.success) setCategories(res.data.data || []);
      else setError(res.data.message);
    } catch (e) { setError('Failed to load categories'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); api.get('/gst/list.php').then(r => { if (r.data.success) setGstRates(r.data.data || []); }).catch(() => {}); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', parent_id: '', gst_rate_id: '', hsn_code: '', status: 'active' });
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name || '', description: c.description || '', parent_id: c.parent_id || '', gst_rate_id: c.gst_rate_id || '', hsn_code: c.hsn_code || '', status: c.status || 'active' });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, parent_id: form.parent_id || '' };
      if (editing) {
        await api.post('/categories/update.php', { id: editing.id, ...payload });
      } else {
        await api.post('/categories/create.php', payload);
      }
      setShowForm(false);
      fetch();
    } catch (e) { setError('Failed to save category'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category? Products in this category will be uncategorized.')) return;
    try { await api.post('/categories/delete.php', { id }); fetch(); }
    catch (e) { setError('Failed to delete category'); }
  };

  if (loading) return <div className="text-center py-20 text-gray-600 dark:text-gray-400">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Categories</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage product categories: {categories.length} categories</p>
        </div>
        <button onClick={openCreate} className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors">Add Category</button>
      </div>

      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">{error}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">HSN Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">GST Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {categories.length === 0 ? (
                <tr><td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">No categories found.</td></tr>
              ) : categories.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{c.name}</td>
                  <td className="px-6 py-4 text-sm font-mono text-gray-600 dark:text-gray-400">{c.hsn_code || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{c.parent_name || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{c.gst_rate_name ? `${c.gst_rate_name} (${c.gst_rate}%)` : '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">{c.description || '-'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${c.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'}`}>{c.status}</span>
                  </td>
                  <td className="px-6 py-4 text-sm space-x-2">
                    <button onClick={() => openEdit(c)} className="text-primary-600 hover:text-primary-900 dark:hover:text-primary-400">Edit</button>
                    <button onClick={() => handleDelete(c.id)} className="text-red-600 hover:text-red-900 dark:hover:text-red-400">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Category' : 'Add Category'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
              <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Parent Category</label>
              <select value={form.parent_id} onChange={e => setForm({...form, parent_id: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
                <option value="">None (Top Level)</option>
                {categories.filter(c => !editing || c.id !== editing.id).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">GST Rate</label>
              <select value={form.gst_rate_id} onChange={e => setForm({...form, gst_rate_id: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
                <option value="">None</option>
                {gstRates.map(g => <option key={g.id} value={g.id}>{g.name} ({g.rate}%)</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">HSN Code</label>
              <input type="text" value={form.hsn_code} onChange={e => setForm({...form, hsn_code: e.target.value})} placeholder="e.g. 8517, 6204" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows="3" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"></textarea>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
            <button type="submit" className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors">{editing ? 'Update' : 'Create'} Category</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Categories;
